import React, { useState, useEffect, useContext, useCallback, memo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../components/auth/AuthProvider';

const ProfilePage = () => {
    const { user, updateProfile, updatePassword, logout, getUserOrders, getUserProducts } = useContext(AuthContext);

    const [activeTab, setActiveTab] = useState('profile'); // 'profile', 'password'
    const [orders, setOrders] = useState([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [ordersError, setOrdersError] = useState('');
    const [products, setProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);
    const [productsError, setProductsError] = useState('');

    // Profile info states
    const [username, setUsername] = useState(user?.username || '');
    const [firstName, setFirstName] = useState(user?.first_name || '');
    const [lastName, setLastName] = useState(user?.last_name || '');
    const [email, setEmail] = useState(user?.email || '');
    const [profileMessage, setProfileMessage] = useState({ type: '', text: '' });

    // Password states
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });

    // Animation control ref to prevent re-animation on input change
    const profileAnimationRef = useRef(null);
    const passwordAnimationRef = useRef(null);

    const fetchOrders = async () => {
        try {
            setOrdersLoading(true);
            setOrdersError('');
            const result = await getUserOrders();

            if (result.success) {
                setOrders(result.orders || []);
            } else {
                setOrdersError(result.message || 'Failed to load orders');
            }
        } catch (error) {
            setOrdersError('Error loading orders');
        } finally {
            setOrdersLoading(false);
        }
    };

    const fetchProducts = async () => {
        try {
            setProductsLoading(true);
            setProductsError('');
            const result = await getUserProducts();

            if (result.success) {
                // Process the map_data which might be stored as a JSON string
                const processedProducts = (result.products || []).map(product => {
                    if (product.map_data && typeof product.map_data === 'string') {
                        try {
                            product.map_data = JSON.parse(product.map_data);
                        } catch (e) {
                            console.error('Error parsing map_data:', e);
                        }
                    }
                    return product;
                });
                setProducts(processedProducts);
            } else {
                setProductsError(result.message || 'Failed to load star maps');
            }
        } catch (error) {
            setProductsError('Error loading star maps');
        } finally {
            setProductsLoading(false);
        }
    };

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        setProfileMessage({ type: '', text: '' });

        if (!username || !email) {
            setProfileMessage({ type: 'error', text: 'Username and email are required' });
            return;
        }

        const result = await updateProfile(username, email, firstName, lastName);

        if (result.success) {
            setProfileMessage({ type: 'success', text: 'Profile updated successfully' });
        } else {
            setProfileMessage({ type: 'error', text: result.message || 'Profile update failed' });
        }
    };

    const handlePasswordUpdate = async (e) => {
        e.preventDefault();
        setPasswordMessage({ type: '', text: '' });

        if (!currentPassword || !newPassword || !confirmPassword) {
            setPasswordMessage({ type: 'error', text: 'Please fill in all fields' });
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
            return;
        }

        if (newPassword.length < 6) {
            setPasswordMessage({ type: 'error', text: 'Password must be at least 6 characters' });
            return;
        }

        const result = await updatePassword(currentPassword, newPassword);

        if (result.success) {
            setPasswordMessage({ type: 'success', text: 'Password updated successfully' });
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } else {
            setPasswordMessage({ type: 'error', text: result.message || 'Password update failed' });
        }
    };

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'EUR'
        }).format(price);
    };

    // Generate a high-definition version download URL
    const getHdDownloadUrl = (productId) => {
        return `/api/downloads/hd-map-${productId}.jpg`;
    };

    // Tab content components
    const ProfileInformation = memo(() => {
        const { user, updateProfile } = useContext(AuthContext);
        const [username, setUsername] = useState(user?.username || '');
        const [firstName, setFirstName] = useState(user?.first_name || '');
        const [lastName, setLastName] = useState(user?.last_name || '');
        const [email, setEmail] = useState(user?.email || '');
        const [profileMessage, setProfileMessage] = useState({ type: '', text: '' });
        const profileAnimationRef = useRef(null);

        // Add state for orders and products
        const [orders, setOrders] = useState([]);
        const [ordersLoading, setOrdersLoading] = useState(false);
        const [ordersError, setOrdersError] = useState('');
        const [products, setProducts] = useState([]);
        const [productsLoading, setProductsLoading] = useState(false);
        const [productsError, setProductsError] = useState('');

        const { getUserOrders, getUserProducts } = useContext(AuthContext);

        useEffect(() => {
            if (profileAnimationRef.current) {
                profileAnimationRef.current.classList.add('animate-fade-in-up');
            }

            // Fetch orders and products when component mounts
            fetchOrders();
            fetchProducts();

            return () => {
                if (profileAnimationRef.current) {
                    profileAnimationRef.current.classList.remove('animate-fade-in-up');
                }
            };
        }, []);

        const fetchOrders = async () => {
            try {
                setOrdersLoading(true);
                setOrdersError('');
                const result = await getUserOrders();

                if (result.success) {
                    setOrders(result.orders || []);
                } else {
                    setOrdersError(result.message || 'Failed to load orders');
                }
            } catch (error) {
                setOrdersError('Error loading orders');
            } finally {
                setOrdersLoading(false);
            }
        };

        const fetchProducts = async () => {
            try {
                setProductsLoading(true);
                setProductsError('');
                const result = await getUserProducts();

                if (result.success) {
                    // Process the map_data which might be stored as a JSON string
                    const processedProducts = (result.products || []).map(product => {
                        if (product.map_data && typeof product.map_data === 'string') {
                            try {
                                product.map_data = JSON.parse(product.map_data);
                            } catch (e) {
                                console.error('Error parsing map_data:', e);
                            }
                        }
                        return product;
                    });
                    setProducts(processedProducts);
                } else {
                    setProductsError(result.message || 'Failed to load star maps');
                }
            } catch (error) {
                setProductsError('Error loading star maps');
            } finally {
                setProductsLoading(false);
            }
        };

        const handleProfileUpdate = async (e) => {
            e.preventDefault();
            setProfileMessage({ type: '', text: '' });

            if (!username || !email) {
                setProfileMessage({ type: 'error', text: 'Username and email are required' });
                return;
            }

            const result = await updateProfile(username, email, firstName, lastName);

            if (result.success) {
                setProfileMessage({ type: 'success', text: 'Profile updated successfully' });
            } else {
                setProfileMessage({ type: 'error', text: result.message || 'Profile update failed' });
            }
        };

        return (
            <div ref={profileAnimationRef}>
                {profileMessage.text && (
                    <div className={`mb-6 px-4 py-3 rounded ${profileMessage.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                        {profileMessage.text}
                    </div>
                )}

                <form onSubmit={handleProfileUpdate}>
                    <div className="mb-5">
                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="username">
                            Username
                        </label>
                        <input
                            className="appearance-none border border-gray-300 rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
                            id="username"
                            type="text"
                            placeholder="Username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="firstName">
                                First Name
                            </label>
                            <input
                                className="appearance-none border border-gray-300 rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
                                id="firstName"
                                type="text"
                                placeholder="First Name"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="lastName">
                                Last Name
                            </label>
                            <input
                                className="appearance-none border border-gray-300 rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
                                id="lastName"
                                type="text"
                                placeholder="Last Name"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="mb-6">
                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">
                            Email
                        </label>
                        <input
                            className="appearance-none border border-gray-300 rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
                            id="email"
                            type="email"
                            placeholder="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>

                    <div className="flex justify-end">
                        <button
                            className="bg-accent hover:bg-opacity-90 text-primary font-bold py-3 px-6 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-all"
                            type="submit"
                        >
                            Update Profile
                        </button>
                    </div>
                </form>

                {/* Orders Section */}
                <div className="mt-12 pt-6 border-t border-gray-200">
                    <h3 className="text-xl font-bold mb-4 text-primary">Your Orders</h3>

                    {ordersError && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                            {ordersError}
                        </div>
                    )}

                    {ordersLoading ? (
                        <div className="flex justify-center my-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent"></div>
                        </div>
                    ) : orders.length === 0 ? (
                        <div className="text-center py-8">
                            <p className="text-gray-600 mb-4">You haven't placed any orders yet.</p>
                            <Link to="/products" className="bg-accent hover:bg-opacity-90 text-primary font-bold py-2 px-4 rounded-lg">
                                Browse Products
                            </Link>
                        </div>
                    ) : (
                        <div className="mb-6 overflow-hidden rounded-lg border border-gray-200">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order #</th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {orders.map((order) => (
                                        <tr key={order.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">#{order.id}</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatDate(order.created_at)}</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{formatPrice(order.total_amount)}</td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${order.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                    order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-gray-100 text-gray-800'
                                                    }`}>
                                                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Star Maps Section */}
                <div className="mt-12 pt-6 border-t border-gray-200">
                    <h3 className="text-xl font-bold mb-4 text-primary">Your Star Maps</h3>

                    {productsError && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                            {productsError}
                        </div>
                    )}

                    {productsLoading ? (
                        <div className="flex justify-center my-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent"></div>
                        </div>
                    ) : products.length === 0 ? (
                        <div className="text-center py-8">
                            <p className="text-gray-600 mb-4">You haven't created any star maps yet.</p>
                            <Link to="/create" className="bg-accent hover:bg-opacity-90 text-primary font-bold py-2 px-4 rounded-lg">
                                Create Your First Star Map
                            </Link>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {products.map((product) => (
                                <div key={product.id} className="bg-gray-50 rounded-lg overflow-hidden shadow-sm border border-gray-200">
                                    <div className="relative h-48 overflow-hidden">
                                        {product.image_url ? (
                                            <img
                                                src={product.image_url}
                                                alt={product.product_name}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center bg-gray-200">
                                                <span className="text-gray-500">No Image</span>
                                            </div>
                                        )}
                                        {product.order_status && product.order_status !== 'completed' && (
                                            <div className="absolute top-2 right-2 bg-amber-500 text-white px-2 py-1 rounded-md text-xs font-medium">
                                                Pending
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-4">
                                        <h3 className="text-lg font-semibold mb-2">{product.product_name}</h3>
                                        <div className="text-sm text-gray-600 mb-2">Created on {formatDate(product.created_at)}</div>
                                        <div className="text-primary font-medium mb-3">{formatPrice(product.price)}</div>

                                        {product.map_data && typeof product.map_data === 'object' && (
                                            <div className="bg-white p-3 rounded text-sm mb-3 border border-gray-100">
                                                <p className="font-semibold mb-1">Map Details:</p>
                                                <div className="grid grid-cols-2 gap-2">
                                                    {product.map_data.date && (
                                                        <>
                                                            <span className="text-gray-600">Date:</span>
                                                            <span>{product.map_data.date}</span>
                                                        </>
                                                    )}
                                                    {product.map_data.location && (
                                                        <>
                                                            <span className="text-gray-600">Location:</span>
                                                            <span>{product.map_data.location}</span>
                                                        </>
                                                    )}
                                                    {product.map_data.caption && (
                                                        <>
                                                            <span className="text-gray-600">Caption:</span>
                                                            <span>{product.map_data.caption}</span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        <div className="flex flex-col gap-3 mt-4">
                                            {/* Conditionally render PDF download button */}
                                            {(product.id || product.pdf_url) && (
                                                <a
                                                    href={`${product.pdf_url || `/downloads/starmap_${product.id}.pdf`}?t=${Date.now()}`}
                                                    download={`star-map-${product.id}.pdf`}
                                                    className={`text-center ${product.order_status === 'completed' ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-400 hover:bg-gray-500'} text-white py-2 px-4 rounded-lg font-medium text-sm`}
                                                >
                                                    Download PDF{product.order_status !== 'completed' ? ' (Preview)' : ''}
                                                </a>
                                            )}

                                            <Link
                                                to="/create"
                                                className="text-center border border-primary text-primary hover:bg-primary hover:text-white py-2 px-4 rounded-lg font-medium text-sm"
                                            >
                                                Create Similar
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    });

    const PasswordSettings = memo(() => {
        const { updatePassword } = useContext(AuthContext);
        const [currentPassword, setCurrentPassword] = useState('');
        const [newPassword, setNewPassword] = useState('');
        const [confirmPassword, setConfirmPassword] = useState('');
        const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });
        const passwordAnimationRef = useRef(null);

        useEffect(() => {
            if (passwordAnimationRef.current) {
                passwordAnimationRef.current.classList.add('animate-fade-in-up');
            }

            return () => {
                if (passwordAnimationRef.current) {
                    passwordAnimationRef.current.classList.remove('animate-fade-in-up');
                }
            };
        }, []);

        const handlePasswordUpdate = async (e) => {
            e.preventDefault();
            setPasswordMessage({ type: '', text: '' });

            if (!currentPassword || !newPassword || !confirmPassword) {
                setPasswordMessage({ type: 'error', text: 'Please fill in all fields' });
                return;
            }

            if (newPassword !== confirmPassword) {
                setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
                return;
            }

            if (newPassword.length < 6) {
                setPasswordMessage({ type: 'error', text: 'Password must be at least 6 characters' });
                return;
            }

            const result = await updatePassword(currentPassword, newPassword);

            if (result.success) {
                setPasswordMessage({ type: 'success', text: 'Password updated successfully' });
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
            } else {
                setPasswordMessage({ type: 'error', text: result.message || 'Password update failed' });
            }
        };

        return (
            <div ref={passwordAnimationRef}>
                {passwordMessage.text && (
                    <div className={`mb-6 px-4 py-3 rounded ${passwordMessage.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                        {passwordMessage.text}
                    </div>
                )}

                <form onSubmit={handlePasswordUpdate}>
                    <div className="mb-5">
                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="currentPassword">
                            Current Password
                        </label>
                        <input
                            className="appearance-none border border-gray-300 rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
                            id="currentPassword"
                            type="password"
                            placeholder="Current Password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                        />
                    </div>

                    <div className="mb-5">
                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="newPassword">
                            New Password
                        </label>
                        <input
                            className="appearance-none border border-gray-300 rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
                            id="newPassword"
                            type="password"
                            placeholder="New Password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                        />
                    </div>

                    <div className="mb-6">
                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="confirmPassword">
                            Confirm New Password
                        </label>
                        <input
                            className="appearance-none border border-gray-300 rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
                            id="confirmPassword"
                            type="password"
                            placeholder="Confirm New Password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                    </div>

                    <div className="flex justify-end">
                        <button
                            className="bg-accent hover:bg-opacity-90 text-primary font-bold py-3 px-6 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-all"
                            type="submit"
                        >
                            Update Password
                        </button>
                    </div>
                </form>
            </div>
        );
    });

    // Navigation link with icon
    const NavLink = memo(({ id, icon, label, onClick, isActive }) => (
        <div
            onClick={onClick}
            className={`flex items-center space-x-3 px-4 py-3 cursor-pointer rounded-lg mb-2 transition-all ${isActive
                ? 'bg-primary text-white shadow-md'
                : 'hover:bg-gray-100 text-gray-700'
                }`}
        >
            <div className={isActive ? 'text-white' : 'text-primary'}>
                {icon}
            </div>
            <span className="font-medium">{label}</span>
        </div>
    ));

    // Get username and email for sidebar display only
    const displayUsername = user?.username || '';
    const displayEmail = user?.email || '';

    return (
        <div className="bg-gray-50" style={{ paddingTop: '30px', paddingBottom: '50px' }}>
            <div className="container mx-auto px-4 py-16">
                <h1 className="text-3xl font-bold mb-6 text-primary">Your Account</h1>

                <div className="flex flex-col md:flex-row gap-6">
                    {/* Sidebar */}
                    <div className="w-full md:w-72">
                        <div className="bg-white p-6 rounded-lg shadow-sm">
                            <div className="flex flex-col items-center mb-6 pb-6 border-b border-gray-200">
                                <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center text-white text-2xl font-bold mb-3">
                                    {displayUsername.charAt(0).toUpperCase()}
                                </div>
                                <div className="text-center">
                                    <h2 className="text-lg font-bold text-gray-800">{displayUsername}</h2>
                                    <p className="text-gray-500 text-sm">{displayEmail}</p>
                                </div>
                            </div>

                            <nav className="space-y-1">
                                <NavLink
                                    id="profile"
                                    isActive={activeTab === 'profile'}
                                    onClick={() => setActiveTab('profile')}
                                    icon={
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                    }
                                    label="Profile Information"
                                />

                                <NavLink
                                    id="password"
                                    isActive={activeTab === 'password'}
                                    onClick={() => setActiveTab('password')}
                                    icon={
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                    }
                                    label="Password"
                                />

                                <div
                                    onClick={logout}
                                    className="flex items-center space-x-3 px-4 py-3 cursor-pointer rounded-lg mt-6 text-red-600 hover:bg-red-50 transition-all"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                    </svg>
                                    <span className="font-medium">Log Out</span>
                                </div>
                            </nav>
                        </div>
                    </div>

                    {/* Main Content Area */}
                    <div className="w-full md:flex-1">
                        <div className="bg-white p-8 rounded-lg shadow-sm">
                            {activeTab === 'profile' && (
                                <>
                                    <h2 className="text-2xl font-bold mb-6 text-primary">Profile Information</h2>
                                    <ProfileInformation />
                                </>
                            )}

                            {activeTab === 'password' && (
                                <>
                                    <h2 className="text-2xl font-bold mb-6 text-primary">Password Settings</h2>
                                    <PasswordSettings />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfilePage; 